import { Suspense, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { AlertCircle, Bell, Check, CloudOff, HeartPulse, Home, LogOut, MoreHorizontal, RefreshCw, Search, Settings, UserRound, Users } from 'lucide-react';
import { brand } from '@app/shared/brand';
import { useAppLock } from '../platform/appLock.ts';
import { db } from '../platform/db.ts';
import { useOnline, useSyncStatus } from '../platform/hooks.ts';
import { refreshInbox, useUnreadCount } from '../platform/notifications.ts';
import { useSession } from '../platform/session.tsx';
import { syncEngine } from '../platform/sync/engine.ts';
import { useToast } from '../platform/toast.tsx';
import { Avatar, IconButton, Menu, Spinner } from '../ui/index.ts';
import { LockScreen } from './LockScreen.tsx';
import { useModules } from './ModulesContext.tsx';

function SyncPill() {
  const status = useSyncStatus();
  const online = useOnline();
  const pending = useLiveQuery(() => db.outbox.count(), []) ?? 0;
  const conflicts = useLiveQuery(() => db.conflicts.count(), []) ?? 0;
  const failedUploads = useLiveQuery(() => db.uploads.filter((u) => u.state === 'failed').count(), []) ?? 0;
  const navigate = useNavigate();
  let cls = 'shell-status';
  let icon = <Check aria-hidden="true" />;
  let label = 'Up to date';
  if (conflicts > 0 || failedUploads > 0) {
    cls += ' shell-status--attention';
    icon = <AlertCircle aria-hidden="true" />;
    label = conflicts > 0 ? `${conflicts} to review` : 'Upload problem';
  } else if (!online || status.phase === 'offline') {
    cls += ' shell-status--offline';
    icon = <CloudOff aria-hidden="true" />;
    label = pending ? `Offline · ${pending} waiting` : 'Offline';
  } else if (status.phase === 'syncing' || pending > 0) {
    icon = <RefreshCw aria-hidden="true" />;
    label = pending ? `Syncing ${pending}` : 'Syncing';
  } else if (status.phase === 'error') {
    cls += ' shell-status--attention';
    icon = <AlertCircle aria-hidden="true" />;
    label = 'Sync issue';
  }
  return (
    <button
      type="button"
      className={cls}
      onClick={() => (conflicts || failedUploads || status.phase === 'error' ? navigate('/settings/data') : void syncEngine.syncNow())}
      aria-label={`Sync status: ${label}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function FamilyMark() {
  return <img className="shell-family__mark" src={brand.icons.svg} alt="" width={34} height={34} />;
}

export function AppShell() {
  const session = useSession();
  const modules = useModules();
  const unread = useUnreadCount();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const me = session.me!;
  const family = session.activeFamily!;
  const appLock = useAppLock();

  useEffect(() => {
    // Braces matter: newer browsers return a Promise from scrollTo, which must not become the effect cleanup.
    window.scrollTo({ top: 0 });
  }, [location.pathname]);
  useEffect(() => {
    void refreshInbox();
    const onReject = (messages: string[]) => toast(`Couldn't save a change: ${messages[0]}`, { tone: 'error', durationMs: 7000 });
    const onNotify = (n: { title: string; link: string | null }) =>
      toast(n.title, n.link ? { action: { label: 'View', onAction: () => navigate(n.link!) } } : undefined);
    syncEngine.rejections.add(onReject);
    syncEngine.notificationListeners.add(onNotify);
    return () => {
      syncEngine.rejections.delete(onReject);
      syncEngine.notificationListeners.delete(onNotify);
    };
  }, [toast, navigate]);

  if (appLock.locked) return <LockScreen unlock={appLock.unlock} />;

  const navItems = modules.filter((m) => m.nav).sort((a, b) => a.nav!.order - b.nav!.order);
  const groups: { id: 'main' | 'family' | 'household'; label: string | null }[] = [
    { id: 'main', label: null },
    { id: 'family', label: 'Family' },
    { id: 'household', label: 'Household' },
  ];
  const tabs = navItems.filter((m) => m.nav!.bottomTab).slice(0, 3);

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="shell-header">
        {me.families.length > 1 ? (
          <Menu
            label="Switch family"
            trigger={<FamilyMark />}
            items={me.families.map((f) => ({ label: f.id === family.id ? `✓ ${f.name}` : f.name, onSelect: () => session.setActiveFamily(f.id) }))}
          />
        ) : null}
        <Link to="/" className="shell-family" aria-label={`${family.name} — Today`} style={{ textDecoration: 'none', color: 'inherit' }}>
          {me.families.length <= 1 && <FamilyMark />}
          <span className="shell-family__name">{family.name}</span>
        </Link>
        <SyncPill />
        <IconButton label="Search" onClick={() => navigate('/search')}>
          <Search aria-hidden="true" />
        </IconButton>
        <IconButton label={unread ? `Notifications, ${unread} unread` : 'Notifications'} badge={unread > 0} onClick={() => navigate('/inbox')}>
          <Bell aria-hidden="true" />
        </IconButton>
        {modules.some((m) => m.id === 'emergency') && (
          <IconButton label="Emergency info" className="shell-emergency" onClick={() => navigate('/emergency')}>
            <HeartPulse aria-hidden="true" />
          </IconButton>
        )}
        <Menu
          label="Account"
          trigger={<Avatar name={me.user.displayName} color={family.color} size="sm" />}
          items={[
            { label: 'Profile', icon: <UserRound aria-hidden="true" />, onSelect: () => navigate('/settings/profile') },
            { label: 'Family', icon: <Users aria-hidden="true" />, onSelect: () => navigate('/family') },
            { label: 'Settings', icon: <Settings aria-hidden="true" />, onSelect: () => navigate('/settings') },
            { label: 'Sign out', icon: <LogOut aria-hidden="true" />, onSelect: () => void session.signOut() },
          ]}
        />
      </header>
      <div className="shell-body">
        <nav className="sidebar" aria-label="Main">
          <NavLink to="/" end>
            <Home aria-hidden="true" />
            Today
          </NavLink>
          {groups.map((g) => {
            const items = navItems.filter((m) => m.nav!.group === g.id);
            if (!items.length) return null;
            return (
              <div key={g.id} style={{ display: 'contents' }}>
                {g.label && <div className="sidebar__group">{g.label}</div>}
                {items.map((m) => (
                  <NavLink key={m.id} to={m.path}>
                    <m.icon aria-hidden="true" />
                    {m.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
          <div className="sidebar__group">You</div>
          <NavLink to="/family">
            <Users aria-hidden="true" />
            Family
          </NavLink>
          <NavLink to="/settings">
            <Settings aria-hidden="true" />
            Settings
          </NavLink>
        </nav>
        <main id="main" className="shell-main" tabIndex={-1}>
          <Suspense fallback={<Spinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <nav className="bottom-nav" aria-label="Main">
        <NavLink to="/" end>
          <span className="nav-pill">
            <Home aria-hidden="true" />
          </span>
          Today
        </NavLink>
        {tabs.map((m) => (
          <NavLink key={m.id} to={m.path}>
            <span className="nav-pill">
              <m.icon aria-hidden="true" />
            </span>
            {m.label}
          </NavLink>
        ))}
        <NavLink to="/more">
          <span className="nav-pill">
            <MoreHorizontal aria-hidden="true" />
          </span>
          More
        </NavLink>
      </nav>
    </div>
  );
}
